import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

function AnimatedSection({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      {children}
    </div>
  );
}

const courses = [
  {
    title: "Asas Kecerdasan Buatan (AI)",
    desc: "Pengenalan kepada konsep AI, ChatGPT, dan alat AI untuk produktiviti harian. Sesuai untuk pemula tanpa latar belakang teknologi.",
    duration: "4 minggu",
    level: "Pemula",
    icon: "ri-robot-2-line",
    color: "bg-teal-50",
    iconColor: "text-teal-600",
  },
  {
    title: "Kemahiran Asas Komputer",
    desc: "Belajar menggunakan komputer, internet, e-mel, dan aplikasi pejabat asas (Word, Excel). Sesuai untuk golongan dewasa yang baru bermula.",
    duration: "6 minggu",
    level: "Pemula",
    icon: "ri-computer-line",
    color: "bg-primary-50",
    iconColor: "text-primary-600",
  },
  {
    title: "Pengenalan kepada Pengaturcaraan",
    desc: "Asas logik pengaturcaraan menggunakan Python. Membina projek mudah seperti kalkulator dan permainan teka-teki.",
    duration: "8 minggu",
    level: "Pemula",
    icon: "ri-code-box-line",
    color: "bg-warm-50",
    iconColor: "text-warm-600",
  },
  {
    title: "Digital Skills untuk Kerjaya",
    desc: "Kemahiran digital yang diperlukan di tempat kerja moden: pengurusan e-mel profesional, aplikasi kolaboratif, dan etika digital.",
    duration: "5 minggu",
    level: "Semua Peringkat",
    icon: "ri-briefcase-line",
    color: "bg-purple-50",
    iconColor: "text-purple-600",
  },
  {
    title: "Reka Bentuk Grafik Asas",
    desc: "Pengenalan kepada reka bentuk grafik menggunakan Canva. Membina poster, kad nama, dan media sosial yang menarik.",
    duration: "4 minggu",
    level: "Pemula",
    icon: "ri-palette-line",
    color: "bg-pink-50",
    iconColor: "text-pink-500",
  },
  {
    title: "Perniagaan Digital & E-Dagang",
    desc: "Cara memulakan perniagaan dalam talian, menggunakan platform seperti Shopee, TikTok Shop, dan media sosial untuk pemasaran.",
    duration: "6 minggu",
    level: "Pemula / Sederhana",
    icon: "ri-store-3-line",
    color: "bg-orange-50",
    iconColor: "text-orange-500",
  },
];

const whyJoin = [
  {
    icon: "ri-vip-crown-line",
    title: "Percuma",
    desc: "Semua kursus disediakan percuma untuk golongan miskin, pelarian, dan asnaf.",
  },
  {
    icon: "ri-user-smile-line",
    title: "Fasilitator Berpengalaman",
    desc: "Dibimbing oleh sukarelawan profesional dari industri teknologi.",
  },
  {
    icon: "ri-award-line",
    title: "Sijil Penyertaan",
    desc: "Setiap peserta menerima sijil selepas menamatkan kursus.",
  },
  {
    icon: "ri-community-line",
    title: "Komuniti Sokongan",
    desc: "Akses kepada kumpulan komuniti untuk berkongsi dan belajar bersama.",
  },
];

export default function Training() {
  return (
    <div className="min-h-screen pt-20 md:pt-24">
      {/* Hero */}
      <section className="relative h-[300px] md:h-[380px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20bright%20computer%20training%20classroom%20with%20diverse%20Malaysian%20adults%20using%20laptops%20warm%20natural%20lighting%20clean%20minimal%20interior%20earth%20tone%20palette%20green%20plants%20in%20decor%20friendly%20learning%20atmosphere&width=1600&height=550&seq=trainhero&orientation=landscape"
          alt="Latihan AI dan Komputer"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-teal-900/70" />
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <h1 className="font-display text-3xl md:text-5xl font-bold text-white">
            Latihan AI &amp; Komputer
          </h1>
          <p className="text-white/80 text-sm md:text-base mt-3 max-w-xl">
            Memperkasakan komuniti dengan kemahiran digital untuk masa depan
            yang lebih cerah
          </p>
        </div>
      </section>

      {/* Why join */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <AnimatedSection className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-2">
              Mengapa Sertai Kami?
            </p>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Teknologi Untuk Semua
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-lg mx-auto">
              Kami percaya setiap orang &mdash; tanpa mengira latar belakang &mdash;
              berhak menguasai kemahiran digital.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {whyJoin.map((item) => (
              <div
                key={item.title}
                className="text-center rounded-xl border border-gray-100 p-5 md:p-6 hover:shadow-sm transition-shadow"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-50 mx-auto mb-4">
                  <i className={`${item.icon} text-teal-600 text-xl`} />
                </div>
                <h3 className="font-semibold text-sm md:text-base text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </section>

      {/* Courses */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-earth-50">
        <AnimatedSection className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-2">
              Kursus Ditawarkan
            </p>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Senarai Program Latihan
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-lg mx-auto">
              Pilih kursus yang sesuai dengan minat dan matlamat anda.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {courses.map((course) => (
              <div
                key={course.title}
                className="rounded-xl border border-gray-100 bg-white overflow-hidden hover:shadow-sm transition-shadow"
              >
                <div className={`${course.color} h-32 flex items-center justify-center`}>
                  <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <i className={`${course.icon} ${course.iconColor} text-2xl`} />
                  </div>
                </div>
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">
                      {course.level}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-600 text-xs">
                      {course.duration}
                    </span>
                  </div>
                  <h3 className="font-semibold text-sm md:text-base text-gray-900 mb-2">
                    {course.title}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                    {course.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </section>

      {/* How to register */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <AnimatedSection className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-2">
                Pendaftaran
              </p>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Cara Mendaftar
              </h2>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6">
                Pendaftaran adalah percuma dan terbuka kepada golongan miskin,
                pelarian, asnaf, dan sesiapa yang memerlukan. Ikuti langkah mudah
                berikut:
              </p>

              <div className="space-y-4">
                {[
                  {
                    step: "1",
                    title: "Pilih Kursus",
                    desc: "Lihat senarai kursus dan pilih yang sesuai dengan minat anda.",
                  },
                  {
                    step: "2",
                    title: "Isi Borang Pendaftaran",
                    desc: "Hubungi kami melalui telefon, WhatsApp, atau borang dalam talian.",
                  },
                  {
                    step: "3",
                    title: "Hadiri Sesi Pengenalan",
                    desc: "Kami akan menjadualkan sesi pengenalan sebelum kursus bermula.",
                  },
                  {
                    step: "4",
                    title: "Mulakan Pembelajaran!",
                    desc: "Hadiri kelas mengikut jadual yang ditetapkan dan siapkan tugasan.",
                  },
                ].map((s) => (
                  <div key={s.step} className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-teal-700 text-white text-sm font-bold shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {s.title}
                      </div>
                      <p className="text-xs md:text-sm text-gray-500">
                        {s.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Malaysian%20students%20of%20different%20ages%20sitting%20together%20in%20a%20bright%20classroom%20learning%20with%20laptops%20and%20notebooks%20warm%20natural%20lighting%20friendly%20atmosphere%20earth%20tone%20palette%20green%20and%20beige%20accents%20educational%20photography&width=600&height=450&seq=trainreg&orientation=landscape"
                alt="Peserta latihan"
                className="w-full rounded-xl object-cover h-64 md:h-80"
              />
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-teal-50">
        <AnimatedSection className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Sedia untuk Memulakan Perjalanan Digital Anda?
          </h2>
          <p className="text-sm md:text-base text-gray-600 max-w-lg mx-auto mb-8">
            Hubungi kami sekarang untuk mendaftar atau mengetahui lebih lanjut
            tentang program latihan kami.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-700 text-white text-sm font-semibold rounded-full hover:bg-teal-800 transition-colors"
            >
              Daftar Sekarang <i className="ri-arrow-right-line" />
            </Link>
            <a
              href="https://wa.me/60123456789"
              className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 text-sm font-medium rounded-full hover:bg-white transition-colors"
              rel="nofollow"
            >
              <i className="ri-whatsapp-line" /> WhatsApp Kami
            </a>
          </div>
        </AnimatedSection>
      </section>
    </div>
  );
}